import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  query,
  where,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';
import { imageProcessingService } from './image-processing-service';
import { User, UserProfile, GuestWriterProfile } from '../types/content';
import { revalidationService } from './revalidation-service';
import { handleCriticalError, handleUserError, handleSilentError } from './error-handler';

/**
 * Profile Service - User Self-Service Operations
 * 
 * Handles operations users perform on their own profiles:
 * - Update own profile (name, bio, image, links)
 * - Upload/manage own profile image
 * - Manage guest writers (pen names/anonymous authors)
 * 
 * For admin user management operations (create/delete users, change roles, view all users),
 * see user-management-service.ts
 * 
 * @example
 * // Update current user's profile
 * await profileService.updateUserProfile(userId, { bio: 'New bio', homepage: 'https://example.com' });
 * 
 * // Upload profile image
 * const imageUrl = await profileService.uploadProfileImage(userId, imageFile);
 */
export class ProfileService {
  private usersCollection = 'users';
  private guestWritersCollection = 'guest_writers';

  private getDb() {
    if (!db) {
      handleCriticalError(new Error('Database not initialized'), 'ProfileService.getDb');
    }
    return db;
  }

  private getStorage() {
    if (!storage) {
      handleCriticalError(new Error('Storage not initialized'), 'ProfileService.getStorage');
    }
    return storage;
  }

  // Update user profile
  async updateUserProfile(userId: string, profileData: Partial<UserProfile>): Promise<void> {
    try {
      const database = this.getDb();
      const docRef = doc(database, this.usersCollection, userId);

      // Check if Twitter handle is being updated and apply rate limiting
      if (profileData.twitterUsername !== undefined) {
        const userDoc = await getDoc(docRef);

        if (userDoc.exists()) {
          const userData = userDoc.data() as User;

          // Only check rate limiting if the Twitter username is actually changing
          if (profileData.twitterUsername !== userData.twitterUsername) {
            // Only apply rate limiting for writers and editors (not admin/owner)
            if (userData.role === 'writer' || userData.role === 'editor') {
              if (userData.twitterUsernameLastUpdatedAt) {
                const lastUpdate = userData.twitterUsernameLastUpdatedAt instanceof Date
                  ? userData.twitterUsernameLastUpdatedAt
                  : (userData.twitterUsernameLastUpdatedAt as any).toDate();

                const daysSinceUpdate = (Date.now() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24);

                if (daysSinceUpdate < 7) {
                  const nextUpdateDate = new Date(lastUpdate.getTime() + 7 * 24 * 60 * 60 * 1000);
                  throw new Error(
                    `Twitter handle can only be updated once per week. Last updated on ${lastUpdate.toLocaleDateString()}. Next update available on ${nextUpdateDate.toLocaleDateString()}.`
                  );
                }
              }
            }

            // If validation passed and handle is being changed, update the timestamp
            (profileData as any).twitterUsernameLastUpdatedAt = new Date();
          }
        }
      }

      await updateDoc(docRef, {
        ...profileData,
        updatedAt: new Date(),
      });

      // Trigger revalidation for author profile updates (non-critical, fail silently)
      try {
        await revalidationService.revalidateAuthor(userId);
      } catch (revalidationError) {
        handleSilentError(revalidationError, 'ProfileService.updateUserProfile - Revalidation');
      }
    } catch (error) {
      throw handleUserError(error, 'Failed to update profile');
    }
  }

  // Upload profile image with WebP conversion
  async uploadProfileImage(userId: string, file: File): Promise<string> {
    try {
      const firebaseStorage = this.getStorage();
      // Process image - optimize while preserving GIF animation for profiles
      const processedImage = await imageProcessingService.processForProfileImage(file);

      // Generate filename with appropriate extension
      const extension = processedImage.format === 'gif' ? 'gif' : 'webp';
      const fileName = `profile-images/${userId}.${extension}`;
      const storageRef = ref(firebaseStorage, fileName);

      // Upload processed image
      const snapshot = await uploadBytes(storageRef, processedImage.blob);

      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Log optimization results (informational only)
      const savings = imageProcessingService.calculateStorageSavings(
        processedImage.originalSize,
        processedImage.compressedSize
      );

      if (processedImage.format === 'gif') {

      } else {

      }

      return downloadURL;
    } catch (error) {
      throw handleUserError(error, 'Failed to upload profile image');
    }
  }

  // Delete profile image
  async deleteProfileImage(userId: string, imageUrl: string): Promise<void> {
    try {
      const firebaseStorage = this.getStorage();
      // Extract the file path from the URL
      const url = new URL(imageUrl);
      const pathStart = url.pathname.indexOf('/o/') + 3;
      const pathEnd = url.pathname.indexOf('?');
      const filePath = decodeURIComponent(url.pathname.slice(pathStart, pathEnd));

      const storageRef = ref(firebaseStorage, filePath);
      await deleteObject(storageRef);
    } catch (error) {
      throw handleUserError(error, 'Failed to delete profile image');
    }
  }

  // Get all writers (for admin to select article authors)
  async getAllWriters(): Promise<User[]> {
    try {
      const database = this.getDb();
      const q = query(
        collection(database, this.usersCollection),
        where('isActive', '==', true)
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate(),
          twitterUsernameLastUpdatedAt: data.twitterUsernameLastUpdatedAt?.toDate(),
          applicationSubmittedAt: data.applicationSubmittedAt?.toDate(),
          emailVerificationCompletedAt: data.emailVerificationCompletedAt?.toDate(),
          lastActivityAt: data.lastActivityAt?.toDate(),
          deletedAt: data.deletedAt?.toDate(),
        } as User;
      });
    } catch (error) {
      throw handleUserError(error, 'Failed to fetch writers');
    }
  }

  // Guest writer management (admin only)
  async createGuestWriter(guestData: Omit<GuestWriterProfile, 'id' | 'isGuestWriter'>): Promise<string> {
    try {
      const database = this.getDb();
      const docRef = await addDoc(collection(database, this.guestWritersCollection), {
        ...guestData,
        isGuestWriter: true,
        createdAt: new Date(),
      });
      return docRef.id;
    } catch (error) {
      throw handleUserError(error, 'Failed to create guest writer');
    }
  }

  async updateGuestWriter(guestId: string, guestData: Partial<GuestWriterProfile>): Promise<void> {
    try {
      const database = this.getDb();
      const docRef = doc(database, this.guestWritersCollection, guestId);
      await updateDoc(docRef, {
        ...guestData,
        updatedAt: new Date(),
      });
    } catch (error) {
      throw handleUserError(error, 'Failed to update guest writer');
    }
  }

  async deleteGuestWriter(guestId: string): Promise<void> {
    try {
      const database = this.getDb();
      const docRef = doc(database, this.guestWritersCollection, guestId);
      await deleteDoc(docRef);
    } catch (error) {
      throw handleUserError(error, 'Failed to delete guest writer');
    }
  }

  async getAllGuestWriters(): Promise<GuestWriterProfile[]> {
    try {
      const database = this.getDb();
      const querySnapshot = await getDocs(collection(database, this.guestWritersCollection));

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as GuestWriterProfile[];
    } catch (error) {
      throw handleUserError(error, 'Failed to fetch guest writers');
    }
  }

  // Get all available authors (regular users + guest users) for article creation
  async getAllAuthors(): Promise<User[]> {
    try {
      const database = this.getDb();
      const q = query(
        collection(database, this.usersCollection),
        where('isActive', '==', true)
      );
      const querySnapshot = await getDocs(q);

      const allUsers = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate(),
          twitterUsernameLastUpdatedAt: data.twitterUsernameLastUpdatedAt?.toDate(),
          applicationSubmittedAt: data.applicationSubmittedAt?.toDate(),
          emailVerificationCompletedAt: data.emailVerificationCompletedAt?.toDate(),
          lastActivityAt: data.lastActivityAt?.toDate(),
          deletedAt: data.deletedAt?.toDate(),
        } as User;
      });

      // Return all active users (both regular and guest)
      // Guest users are identified by isGuest: true flag
      return allUsers;
    } catch (error) {
      throw handleUserError(error, 'Failed to fetch authors');
    }
  }

  // Upload guest writer profile image with WebP conversion
  async uploadGuestWriterImage(guestId: string, file: File): Promise<string> {
    try {
      const firebaseStorage = this.getStorage();
      // Process image - convert to WebP and optimize for profile
      const processedImage = await imageProcessingService.processForProfileImage(file);

      // Generate filename - always WebP for guest writers
      const fileName = `guest-writers/${guestId}.webp`;
      const storageRef = ref(firebaseStorage, fileName);

      // Upload processed image
      const snapshot = await uploadBytes(storageRef, processedImage.blob);

      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Log optimization results (informational only)
      const savings = imageProcessingService.calculateStorageSavings(
        processedImage.originalSize,
        processedImage.compressedSize
      );
      return downloadURL;
    } catch (error) {
      throw handleUserError(error, 'Failed to upload guest writer image');
    }
  }
}

export const profileService = new ProfileService();

